import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  message,
  Row,
  Select,
  Typography,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { loadCategories } from "../../../application/categories-slice";
import { loadProducts } from "../../../application/products-slice";
import { useAppDispatch, useAppSelector } from "../../../application/store";
import { NewProductAttribute, ProductsRepository, Sort } from "../../../domain";
import { ServiceLocater, Services } from "../../../service-locater";
import { logger } from "../../../utils";
import I18n from "../../i18n";
import NewAttributeModal from "./components/NewAttributeModal";
import { loadColors } from "../../../application/colors-slice";

const { useForm, Item } = Form;
const { Text } = Typography;

export default function NewProduct() {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [form] = useForm();
  const { categories } = useAppSelector((state) => state.categories);

  const stableDispacth = useCallback(dispatch, []);
  useEffect(() => {
    stableDispacth(loadCategories());
  }, [stableDispacth]);

  useEffect(() => {
    stableDispacth(loadColors());
  }, [stableDispacth]);

  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<{
    generalCategoryId?: string;
    subCategoryId?: string;
    categoryId?: string;
  }>({});

  const [newAttributeModalVisible, setNewAttributeModalVisible] = useState(
    false
  );
  const openNewAttributeModal = () => setNewAttributeModalVisible(true);
  const closeNewAttributeModal = () => setNewAttributeModalVisible(false);

  const defaultImages = new Array(16);
  defaultImages.fill(undefined);
  const [images, setImages] = useState<Array<File | undefined>>(defaultImages);
  const [attributes, setAttributes] = useState<NewProductAttribute[]>([]);
  const addAttribute = (attribute: NewProductAttribute) => {
    form.setFields([{ name: "attributes", value: "attribute" }]);
    setAttributes([...attributes, attribute]);
  };
  const [showImagesError, setShowImagesError] = useState(false);

  const submit = async () => {
    try {
      const productImages = [];
      for (let index = 0; index < images.length; index++) {
        const image = images[index];
        if (image !== undefined) productImages.push({ file: image, index });
      }
      if (productImages.length === 0) {
        setShowImagesError(true);
        await form.validateFields();
        return;
      }
      await form.validateFields();
      setShowImagesError(false);
      setIsLoading(true);
      const res = await ServiceLocater.get<ProductsRepository>(
        Services.ProductsRepository
      ).add({
        title: form.getFieldValue("title"),
        description: form.getFieldValue("description"),
        label: form.getFieldValue("label"),
        generalCategoryId: form.getFieldValue("generalCategory"),
        subCategoryId: form.getFieldValue("subCategory"),
        categoryId: form.getFieldValue("category"),
        images: productImages,
        attributes: attributes,
      });
      setIsLoading(false);
      res.fold(
        (_) => {
          message.error(I18n.general.networkError);
        },
        (_) => {
          dispatch(loadProducts(1, Sort.date));
          message.success(I18n.product.success);
          history.push("/products");
        }
      );
    } catch (error) {
      logger.error(error);
    }
  };

  const renderSubCategories = () => {
    if (selectedCategories.generalCategoryId === undefined) return <Fragment />;
    return categories
      .find(
        (generalCategory) =>
          generalCategory.id === selectedCategories.generalCategoryId
      )
      ?.subCategories.map((subCategory) => (
        <Select.Option value={subCategory.id}>
          {subCategory.title}
        </Select.Option>
      ));
  };

  const renderCategories = () => {
    if (selectedCategories.subCategoryId === undefined) return <Fragment />;
    return categories
      .find(
        (generalCategory) =>
          generalCategory.id === selectedCategories.generalCategoryId
      )
      ?.subCategories.find(
        (subCategory) => subCategory.id === selectedCategories.subCategoryId
      )
      ?.categories.map((category) => (
        <Select.Option value={category.id}>{category.title}</Select.Option>
      ));
  };

  return (
    <div style={{ margin: "16px 24px" }}>
      <Form form={form} layout="vertical">
        <Item
          name="title"
          label={I18n.product.title}
          required
          rules={[
            {
              required: true,
              validateTrigger: "onSubmit",
              type: "string",
            },
          ]}
        >
          <Input name="title" maxLength={100} />
        </Item>
        <Item
          name="description"
          label={I18n.product.description}
          required
          rules={[
            {
              required: true,
              validateTrigger: "onSubmit",
              type: "string",
            },
          ]}
        >
          <Input.TextArea name="description" inputMode="text" rows={4} />
        </Item>
        <Item name="label" label="هایلایت">
          <Input name="label" inputMode="text" />
        </Item>
        <Row>
          <Col lg={8} md={8} style={{ paddingLeft: 8 }}>
            <Item name="generalCategory" label={I18n.product.generalCategory}>
              <Select
                placeholder={I18n.product.generalCategory}
                allowClear
                onClear={() => {
                  setSelectedCategories({});
                  form.setFields([
                    { name: "generalCategory", value: undefined },
                    { name: "subCategory", value: undefined },
                    { name: "category", value: undefined },
                  ]);
                }}
                onSelect={(value, option) => {
                  setSelectedCategories({ generalCategoryId: value as string });
                  form.setFields([
                    { name: "subCategory", value: undefined },
                    { name: "category", value: undefined },
                  ]);
                }}
              >
                {categories.map((generalCategory) => (
                  <Select.Option value={generalCategory.id}>
                    {generalCategory.title}
                  </Select.Option>
                ))}
              </Select>
            </Item>
          </Col>
          <Col lg={8} md={8} style={{ padding: "0 8px" }}>
            <Item name="subCategory" label={I18n.product.subCategory}>
              <Select
                placeholder={I18n.product.subCategory}
                allowClear
                onClear={() => {
                  setSelectedCategories({
                    generalCategoryId: selectedCategories.generalCategoryId,
                  });
                  form.setFields([
                    { name: "subCategory", value: undefined },
                    { name: "category", value: undefined },
                  ]);
                }}
                disabled={selectedCategories.generalCategoryId === undefined}
                onSelect={(value, option) => {
                  setSelectedCategories({
                    generalCategoryId: selectedCategories.generalCategoryId,
                    subCategoryId: value as string,
                  });
                  form.setFields([{ name: "category", value: undefined }]);
                }}
              >
                {renderSubCategories()}
              </Select>
            </Item>
          </Col>
          <Col lg={8} md={8} style={{ paddingRight: 8 }}>
            <Item name="category" label={I18n.product.category}>
              <Select
                placeholder={I18n.product.category}
                disabled={selectedCategories.subCategoryId === undefined}
                allowClear
                onClear={() => {
                  setSelectedCategories({
                    generalCategoryId: selectedCategories.generalCategoryId,
                    subCategoryId: selectedCategories.subCategoryId,
                  });
                  form.setFields([{ name: "category", value: undefined }]);
                }}
                onSelect={(value, option) =>
                  setSelectedCategories({
                    generalCategoryId: selectedCategories.generalCategoryId,
                    subCategoryId: selectedCategories.subCategoryId,
                    categoryId: value as string,
                  })
                }
              >
                {renderCategories()}
              </Select>
            </Item>
          </Col>
        </Row>
        <Row style={{ height: 40, marginBottom: 8 }} align="middle">
          <Typography.Text>{I18n.product.images}</Typography.Text>
        </Row>
        <Row style={{ marginBottom: 8 }}>
          {images.map((_, index) => {
            return (
              <input
                id={`image-input${index}`}
                type="file"
                style={{ display: "none" }}
                onChange={(e) => {
                  if (e.target.files !== null) {
                    const imagesCopy = [...images];
                    imagesCopy[index] = e.target.files[0];
                    setImages(imagesCopy);
                  }
                }}
              />
            );
          })}
          {images?.map((image, index) => (
            <Col xs={4} style={{ padding: 2 }}>
              {image === undefined ? (
                <div
                  style={{
                    backgroundColor: "#f9f9f9",
                    width: "100%",
                    minHeight: 150,
                    cursor: "pointer",
                    position: "relative",
                  }}
                  onClick={() =>
                    document.getElementById(`image-input${index}`)?.click()
                  }
                >
                  <PlusOutlined
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      WebkitTransform: "translate(-50%, -50%)",
                      transform: "translate(-50%, -50%)",
                    }}
                  />
                </div>
              ) : (
                <Col>
                  <img
                    width="100%"
                    src={URL.createObjectURL(image)}
                    alt={index.toString()}
                  />
                  <Row justify="space-around">
                    <Button
                      type="link"
                      onClick={() =>
                        document.getElementById(`image-input${index}`)?.click()
                      }
                    >
                      <EditOutlined />
                    </Button>
                    <Button
                      type="link"
                      danger
                      onClick={() => {
                        const imagesCopy = [...images];
                        imagesCopy[index] = undefined;
                        setImages(imagesCopy);
                      }}
                    >
                      <DeleteOutlined />
                    </Button>
                  </Row>
                </Col>
              )}
            </Col>
          ))}
        </Row>
        <Row style={{ marginBottom: 24 }}>
          {showImagesError ? (
            <Typography.Text style={{ color: "#ff4d4f", fontSize: 14 }}>
              فیلد تصاویر اجباریست
            </Typography.Text>
          ) : (
            <Fragment />
          )}
        </Row>
        <Item
          name="attributes"
          label={I18n.product.attributes}
          required
          rules={[{ required: true, validateTrigger: "onSubmit" }]}
        >
          <Row align="middle">
            {attributes.map((attribute, index) => (
              <Col lg={12} md={12}>
                <Card
                  bodyStyle={{ padding: 16 }}
                  style={
                    index % 2 === 0 ? { marginLeft: 12 } : { marginRight: 12 }
                  }
                >
                  <Row>
                    <div>
                      <Text>{I18n.product.color}</Text>
                      <br />
                      <Text>{I18n.product.size}</Text>
                      <br />
                      <Text>{I18n.product.sizeDescription}</Text>
                      <br />
                      <Text>{I18n.product.count}</Text>
                      <br />
                      <Text>{I18n.product.price}</Text>
                    </div>
                    <div style={{ paddingRight: 16 }}>
                      <Row align="middle">
                        <div
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: 10,
                            marginLeft: 8,
                            backgroundColor: attribute.color?.color,
                            display: "inline-block",
                          }}
                        />
                        <Text>{attribute.color?.title}</Text>
                      </Row>
                      <Text>{attribute.size}</Text>
                      <br />
                      <Text>{attribute.sizeDescription}</Text>
                      <br />
                      <Text>{attribute.count}</Text>
                      <br />
                      <Text>{attribute.price.toLocaleString()} تومان</Text>
                    </div>
                  </Row>
                </Card>
              </Col>
            ))}
            <Col lg={12} md={12}>
              <Button
                type="link"
                size="middle"
                onClick={openNewAttributeModal}
                icon={<PlusOutlined />}
                block
                style={{ margin: "24px 0" }}
              >
                {I18n.product.addAttribute}
              </Button>
            </Col>
          </Row>
        </Item>
        <Row>
          <Col lg={8} md={8} />
          <Col lg={8} md={8} style={{ padding: "0 8px" }}>
            <Item>
              <Button type="primary" block onClick={submit} loading={isLoading}>
                {I18n.general.submit}
              </Button>
            </Item>
          </Col>
          <Col lg={8} md={8} />
        </Row>
      </Form>

      <NewAttributeModal
        visible={newAttributeModalVisible}
        onOk={addAttribute}
        onClose={closeNewAttributeModal}
      />
    </div>
  );
}
